import { useLazyQuery } from '@apollo/react-hooks';
import graphqlTag from 'graphql-tag';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { useUpdateEventPayoutStatus } from 'components/Events/gql';
import ModalV2 from 'components/ModalV2';
import { PrintEventPerformanceReport } from 'pages/app/Events/EventsReport/Reports/EventPerformanceReport';

const GET_EVENT_PAYOUT_DETAIL = graphqlTag`
  query GetEventPayoutDetail($input: GetEventInput!) {
    getEvent(input: $input) {
      id
      sales
      payout_status
    }
  }
`;

const isDark = (props) => props.theme.name === 'dark';

const Panel = styled.div`
  position: relative;
  width: calc(100vw - 32px);
  max-width: 720px;
  background: ${(props) =>
    isDark(props)
      ? 'linear-gradient(180deg, rgba(26, 26, 46, 0.98) 0%, rgba(15, 15, 35, 0.99) 100%)'
      : '#ffffff'};
  border: 1px solid
    ${(props) =>
      isDark(props) ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 23, 42, 0.08)'};
  border-radius: 16px;
  box-shadow: ${(props) =>
    isDark(props)
      ? '0 24px 64px rgba(0, 0, 0, 0.55)'
      : '0 20px 48px rgba(15, 23, 42, 0.18)'};
  color: ${(props) => (isDark(props) ? '#ffffff' : '#0f172a')};
  overflow: hidden;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${(props) =>
    isDark(props) ? 'rgba(255, 255, 255, 0.05)' : 'rgba(15, 23, 42, 0.05)'};
  border: 1px solid
    ${(props) =>
      isDark(props) ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 23, 42, 0.08)'};
  color: ${(props) => (isDark(props) ? '#94a3b8' : '#64748b')};
  cursor: pointer;
  transition: all 150ms ease-in-out;
  z-index: 1;

  &:hover {
    background: ${(props) =>
      isDark(props) ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)'};
    color: ${(props) => (isDark(props) ? '#ffffff' : '#0f172a')};
  }
`;

const Split = styled.div`
  display: grid;
  grid-template-columns: 1.15fr 1fr;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Left = styled.div`
  padding: 28px;
  border-right: 1px solid
    ${(props) =>
      isDark(props) ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 23, 42, 0.08)'};
  background: ${(props) =>
    isDark(props) ? 'rgba(255, 255, 255, 0.02)' : 'rgba(15, 23, 42, 0.02)'};

  @media (max-width: 640px) {
    border-right: none;
    border-bottom: 1px solid
      ${(props) =>
        isDark(props) ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 23, 42, 0.08)'};
  }
`;

const Right = styled.div`
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: center;
`;

const Title = styled.h3`
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
  padding-right: 36px;
`;

const Account = styled.p`
  color: ${(props) => (isDark(props) ? '#7c8a9d' : '#64748b')};
  font-size: 13px;
  margin: 0 0 24px;
`;

const StatBlock = styled.div`
  margin-bottom: 20px;
`;

const StatLabel = styled.p`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${(props) => (isDark(props) ? '#94a3b8' : '#64748b')};
  margin: 0 0 6px;
  font-weight: 600;
`;

const StatValue = styled.p`
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0;
  color: ${(props) => (isDark(props) ? '#ffffff' : '#0f172a')};
  min-height: 38px;
`;

const StatPlaceholder = styled.span`
  display: inline-block;
  width: 140px;
  height: 28px;
  border-radius: 6px;
  background: ${(props) =>
    isDark(props) ? 'rgba(255, 255, 255, 0.06)' : 'rgba(15, 23, 42, 0.06)'};
  animation: pulse 1.2s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 0.6;
    }
    50% {
      opacity: 1;
    }
  }
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-top: 1px solid
    ${(props) =>
      isDark(props) ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 23, 42, 0.08)'};
  font-size: 13px;
`;

const MetaKey = styled.span`
  color: ${(props) => (isDark(props) ? '#94a3b8' : '#64748b')};
`;

const MetaValue = styled.span`
  color: ${(props) => (isDark(props) ? '#ffffff' : '#0f172a')};
  font-weight: 500;
`;

const STATUS_META = {
  red: {
    label: 'Unpaid',
    color: '#f87171',
    background: 'rgba(239, 68, 68, 0.15)',
    border: 'rgba(239, 68, 68, 0.3)',
  },
  yellow: {
    label: 'Pending',
    color: '#facc15',
    background: 'rgba(234, 179, 8, 0.15)',
    border: 'rgba(234, 179, 8, 0.3)',
  },
  green: {
    label: 'Paid',
    color: '#4ade80',
    background: 'rgba(34, 197, 94, 0.15)',
    border: 'rgba(34, 197, 94, 0.3)',
  },
};

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${(props) => props.$meta.background};
  border: 1px solid ${(props) => props.$meta.border};
  color: ${(props) => props.$meta.color};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const StatusDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  box-shadow: 0 0 0 3px ${(props) => props.$meta.background};
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 150ms ease-in-out;
  border: 1px solid transparent;
  font-family: inherit;
  line-height: 1;
  width: 100%;
  box-sizing: border-box;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
    transform: none !important;
    box-shadow: none !important;
  }

  svg {
    flex-shrink: 0;
  }

  .chev {
    margin-left: auto;
    opacity: 0.7;
  }
`;

const StripeButton = styled(ActionButton)`
  background: #635bff;
  color: #ffffff;

  &:hover:not(:disabled) {
    background: #7a73ff;
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(99, 91, 255, 0.35);
  }
`;

const GhostButton = styled(ActionButton)`
  background: ${(props) =>
    isDark(props) ? 'rgba(255, 255, 255, 0.05)' : 'rgba(15, 23, 42, 0.04)'};
  color: ${(props) => (isDark(props) ? '#ffffff' : '#0f172a')};
  border-color: ${(props) =>
    isDark(props) ? 'rgba(255, 255, 255, 0.15)' : 'rgba(15, 23, 42, 0.12)'};

  &:hover:not(:disabled) {
    background: ${(props) =>
      isDark(props) ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)'};
    border-color: ${(props) =>
      isDark(props) ? 'rgba(255, 255, 255, 0.25)' : 'rgba(15, 23, 42, 0.18)'};
  }
`;

const SuccessButton = styled(ActionButton)`
  background: #22c55e;
  color: #062012;

  &:hover:not(:disabled) {
    filter: brightness(1.05);
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(34, 197, 94, 0.3);
  }
`;

const PaidIndicator = styled(ActionButton).attrs({ as: 'div' })`
  background: rgba(34, 197, 94, 0.12);
  color: #4ade80;
  border-color: rgba(34, 197, 94, 0.3);
  cursor: default;
`;

const UndoButton = styled.button`
  align-self: center;
  margin-top: 2px;
  padding: 6px 10px;
  background: transparent;
  color: ${(props) => (isDark(props) ? '#94a3b8' : '#64748b')};
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 150ms ease-in-out;

  &:hover:not(:disabled) {
    color: ${(props) => (isDark(props) ? '#ffffff' : '#0f172a')};
    background: ${(props) =>
      isDark(props) ? 'rgba(255, 255, 255, 0.05)' : 'rgba(15, 23, 42, 0.05)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ReportWrapper = styled.div`
  display: contents;

  & > div,
  & > div > div {
    display: contents;
  }
`;

const StripeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z" />
  </svg>
);

const DownloadIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ExternalIcon = () => (
  <svg
    className="chev"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="7" y1="17" x2="17" y2="7" />
    <polyline points="7 7 17 7 17 17" />
  </svg>
);

const ChevIcon = () => (
  <svg
    className="chev"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const UndoIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 7v6h6" />
    <path d="M21 17a9 9 0 0 0-15-6.7L3 13" />
  </svg>
);

const formatEventDate = (dateValue) => {
  if (!dateValue) return '—';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

export const PayoutActionsModal = ({
  isOpen,
  onClose,
  event,
  stripeUrl,
  onStatusChanged,
}) => {
  const [fetchEventDetail, { data, loading }] = useLazyQuery(
    GET_EVENT_PAYOUT_DETAIL,
    { fetchPolicy: 'network-only' }
  );
  const updatePayoutStatus = useUpdateEventPayoutStatus();
  const [marking, setMarking] = useState(false);
  const [localStatus, setLocalStatus] = useState(event?.payout_status ?? null);

  const eventId = event?.id;
  const incomingStatus = event?.payout_status;

  useEffect(() => {
    if (isOpen && eventId) {
      fetchEventDetail({ variables: { input: { id: Number(eventId) } } });
      setLocalStatus(incomingStatus ?? null);
    }
  }, [isOpen, eventId, incomingStatus, fetchEventDetail]);

  const effectiveStatus =
    localStatus || data?.getEvent?.payout_status || event?.payout_status;

  const statusMeta = STATUS_META[effectiveStatus] || {
    label: 'Not Set',
    color: '#94a3b8',
    background: 'rgba(148, 163, 184, 0.15)',
    border: 'rgba(148, 163, 184, 0.3)',
  };

  const payoutAmount =
    data?.getEvent?.sales || event?.payout_balance || event?.sales || null;

  const stripeDisabled = !stripeUrl;

  const reportButton = useMemo(
    () => (
      <GhostButton as="div">
        <DownloadIcon />
        Download Payout Report
        <ChevIcon />
      </GhostButton>
    ),
    []
  );

  const handleStripe = () => {
    if (stripeDisabled) return;
    window.open(stripeUrl, '_blank', 'noopener,noreferrer');
  };

  const handleMarkPaid = async () => {
    if (!event?.id || marking || effectiveStatus === 'green') return;
    setMarking(true);
    try {
      const response = await updatePayoutStatus(Number(event.id), 'green');
      if (response) {
        setLocalStatus('green');
        toast.success('Event marked as paid');
        if (onStatusChanged) onStatusChanged();
      }
    } finally {
      setMarking(false);
    }
  };

  const handleUndoPaid = async () => {
    if (!event?.id || marking || effectiveStatus !== 'green') return;
    setMarking(true);
    try {
      const response = await updatePayoutStatus(Number(event.id), 'yellow');
      if (response) {
        setLocalStatus('yellow');
        toast.success('Paid status reverted');
        if (onStatusChanged) onStatusChanged();
      }
    } finally {
      setMarking(false);
    }
  };

  if (!event) return null;

  return (
    <ModalV2 isOpen={isOpen} onClose={onClose}>
      <Panel>
        <CloseButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </CloseButton>

        <Split>
          <Left>
            <Title>{event.name || 'Unnamed Event'}</Title>
            <Account>{event.account_business_dba || '—'}</Account>

            <StatBlock>
              <StatLabel>Payout Amount</StatLabel>
              <StatValue>
                {loading && !payoutAmount ? (
                  <StatPlaceholder />
                ) : (
                  payoutAmount || '—'
                )}
              </StatValue>
            </StatBlock>

            <MetaRow>
              <MetaKey>Event Date</MetaKey>
              <MetaValue>{formatEventDate(event.event_date)}</MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaKey>Stripe Balance</MetaKey>
              <MetaValue>{event.stripe_balance || '—'}</MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaKey>Payout Status</MetaKey>
              <StatusPill $meta={statusMeta}>
                <StatusDot $meta={statusMeta} />
                {statusMeta.label}
              </StatusPill>
            </MetaRow>
          </Left>

          <Right>
            <StripeButton
              type="button"
              onClick={handleStripe}
              disabled={stripeDisabled}
              title={
                stripeDisabled
                  ? 'Stripe account not linked for this event'
                  : 'Open in Stripe'
              }
            >
              <StripeIcon />
              Open in Stripe
              <ExternalIcon />
            </StripeButton>

            <ReportWrapper>
              <PrintEventPerformanceReport
                eventId={event.id}
                button={reportButton}
              />
            </ReportWrapper>

            {effectiveStatus === 'green' ? (
              <>
                <PaidIndicator>
                  <CheckIcon />
                  Event Marked Paid
                </PaidIndicator>
                <UndoButton
                  type="button"
                  onClick={handleUndoPaid}
                  disabled={marking}
                  title="Revert paid status back to pending"
                >
                  <UndoIcon />
                  {marking ? 'Undoing…' : 'Undo mark as paid'}
                </UndoButton>
              </>
            ) : (
              <SuccessButton
                type="button"
                onClick={handleMarkPaid}
                disabled={marking}
              >
                <CheckIcon />
                {marking ? 'Marking…' : 'Mark Event as Paid'}
                <ChevIcon />
              </SuccessButton>
            )}
          </Right>
        </Split>
      </Panel>
    </ModalV2>
  );
};

PayoutActionsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  event: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    event_date: PropTypes.string,
    account_business_dba: PropTypes.string,
    stripe_account_id: PropTypes.string,
    stripe_balance: PropTypes.string,
    payout_balance: PropTypes.string,
    payout_status: PropTypes.oneOf(['red', 'yellow', 'green']),
    sales: PropTypes.string,
  }),
  stripeUrl: PropTypes.string,
  onStatusChanged: PropTypes.func,
};

export default PayoutActionsModal;
